import { useEffect, useMemo, useState } from 'react';
import EditableRecordTable from '../components/EditableRecordTable.jsx';
import DashboardInputLayout from '../components/DashboardInputLayout.jsx';
import DashboardInputToolbar from '../components/DashboardInputToolbar.jsx';
import AdvancedFilterPanel from '../components/AdvancedFilterPanel.jsx';
import useAdvancedFilter from '../hooks/useAdvancedFilter.js';
import {
  createAiManagement,
  deleteAiManagement,
  getActors,
  getAiManagements,
  getEmployees,
  getScheduleOemRegionPairs,
  getStatuses,
  updateAiManagement,
} from '../api/dashboardApi.js';
import { exportToExcel } from '../utils/exportUtils.js';
import ExportConfirmPopup from '../components/ExportConfirmPopup.jsx';
import { getUserName } from '../../../auth/auth.js';
import { canEdit } from '../../../auth/permissions.js';
import { buildLookupMap } from '../utils/converters.js';
import { flattenRow, buildSelectOptions } from '../utils/aiConverters.js';

const AUDIT_COLUMNS = [
  { key: 'createdAt', label: '作成日時', type: 'text', width: '160px', readOnly: true },
  { key: 'createdBy', label: '作成者', type: 'text', width: '120px', readOnly: true },
  { key: 'updatedAt', label: '最終更新日時', type: 'text', width: '160px', readOnly: true },
  { key: 'updatedBy', label: '最終更新者', type: 'text', width: '120px', readOnly: true },
];

const BASE_COLUMNS = [
  { key: 'deadlineCheck', label: '期限チェック', type: 'text', width: '120px', readOnly: true },
  { key: 'task', label: 'タイトル', type: 'text', width: '300px', required: true },
  { key: 'oemRegion', label: 'OEM×対地', type: 'select', width: '180px', required: true },
  { key: 'actors_display', label: '領域', type: 'tags', width: '200px', required: true },
  { key: 'assignees_display', label: '担当者', type: 'tags', width: '200px', required: true },
  { key: 'status_name', label: 'ステータス', type: 'select', width: '140px', required: true },
  { key: 'deadline', label: '対応期限', type: 'date', width: '140px', required: true },
  { key: 'memo', label: '依頼事項・対応内容', type: 'text', width: '260px', required: true },
  ...AUDIT_COLUMNS,
];

export default function DashboardAiManagementPage() {
  const [rawRows, setRawRows] = useState([]);
  const [statusByName, setStatusByName] = useState(new Map());
  const [actorByName, setActorByName] = useState(new Map());
  const [employeeByName, setEmployeeByName] = useState(new Map());
  const [actorOptions, setActorOptions] = useState(['']);
  const [employeeOptions, setEmployeeOptions] = useState(['']);
  const [oemRegionOptions, setOemRegionOptions] = useState(['']);
  const [oemRegionLookup, setOemRegionLookup] = useState(new Map());
  const [showFilters, setShowFilters] = useState(true);
  const [addSignal, setAddSignal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportPopupOpen, setExportPopupOpen] = useState(false);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  const {
    filterValues,
    filterMode,
    setFilterMode,
    handleFieldChange: handleFilterChange,
    clearFilters,
    buildFilterPredicate,
  } = useAdvancedFilter({
    initialValues: {
      deadlineCheck: [],
      oemRegion: [],
      status_name: [],
      actor: [],
      assignee: getUserName() ? [getUserName()] : [],
      date_from: '',
      date_to: '',
      keyword: '',
    },
    initialMode: 'AND',
  });

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError('');
        const [aiData, statusData, actorData, employeeData, oemRegionPairs] = await Promise.all([
          getAiManagements(),
          getStatuses(),
          getActors(),
          getEmployees(),
          getScheduleOemRegionPairs(),
        ]);
        if (!active) return;
        setRawRows(aiData);
        setStatusByName(buildLookupMap(statusData, 'status'));
        setActorByName(buildLookupMap(actorData, 'actor'));
        setEmployeeByName(buildLookupMap(employeeData, 'user_name', 'employee_id'));
        setActorOptions(['', ...actorData.map((item) => item.actor).filter(Boolean)]);
        setEmployeeOptions(['', ...employeeData.map((item) => item.user_name).filter(Boolean)]);

        // OEM×対地 lookup
        const oemRegionOpts = [''];
        const oemRegionMap = new Map();
        oemRegionPairs.forEach((p) => {
          if (!oemRegionMap.has(p.label)) {
            oemRegionOpts.push(p.label);
            oemRegionMap.set(p.label, { oemId: p.oem_id, regionId: p.region_id });
          }
        });
        setOemRegionOptions(oemRegionOpts);
        setOemRegionLookup(oemRegionMap);
      } catch (err) {
        if (!active) return;
        setError('AI管理データの取得に失敗しました');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, []);

  const flatRows = useMemo(() => rawRows.map(flattenRow), [rawRows]);

  const selectColumnKeys = useMemo(
    () => BASE_COLUMNS.filter((col) => col.type === 'select').map((col) => col.key),
    [],
  );

  const selectOptions = useMemo(
    () => buildSelectOptions(flatRows, selectColumnKeys),
    [flatRows, selectColumnKeys],
  );

  const deadlineCheckOptions = useMemo(() => {
    const values = new Set();
    flatRows.forEach((row) => { if (row.deadlineCheck) values.add(row.deadlineCheck); });
    return Array.from(values);
  }, [flatRows]);

  const filteredRows = useMemo(() => {
    const predicate = buildFilterPredicate({
      deadlineCheck: (row, vals) => vals.includes(row.deadlineCheck),
      oemRegion: (row, vals) => vals.includes(row.oemRegion),
      status_name: (row, vals) => vals.includes(row.status_name),
      // 領域・担当者は複数持てるカンマ結合の文字列なので、いずれか1つ含めば一致
      actor: (row, vals) => vals.some((v) => (/* istanbul ignore next -- 領域は必須項目なので空の行は登録できない */ row.actors_display || '').includes(v)),
      assignee: (row, vals) => vals.some((v) => (row.assignees_display || '').includes(v)),
      date_from: (row, val) => {
        const d = row.deadline;
        return d && d >= val;
      },
      date_to: (row, val) => {
        const d = row.deadline;
        return d && d <= val;
      },
      keyword: (row, val) => {
        const keyword = val.trim().toLowerCase();
        if (!keyword) return true;
        // 専用フィルターを持つ項目は対象にせず、残りの列だけを検索する
        const searchable = [row.task, row.memo]
          .filter(Boolean).join(' ').toLowerCase();
        return searchable.includes(keyword);
      },
    });
    return flatRows.filter(predicate);
  }, [flatRows, buildFilterPredicate]);

  const columns = useMemo(
    () => BASE_COLUMNS.map((col) => {
      if (col.key === 'oemRegion') {
        return { ...col, options: oemRegionOptions };
      }
      if (col.key === 'actors_display') {
        return { ...col, getOptions: () => actorOptions };
      }
      if (col.key === 'assignees_display') {
        return { ...col, getOptions: () => employeeOptions };
      }
      if (col.type === 'select') {
        return { ...col, options: /* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ (selectOptions[col.key] || ['']) };
      }
      return col;
    }),
    [oemRegionOptions, actorOptions, employeeOptions, selectOptions],
  );

  const filterFields = useMemo(() => [
    {
      key: 'deadlineCheck',
      label: '期限チェック',
      type: 'multiselect',
      options: deadlineCheckOptions.map((o) => ({ value: o, label: o })),
    },
    {
      key: 'oemRegion',
      label: 'OEM×対地',
      type: 'multiselect',
      options: oemRegionOptions.filter(Boolean).map((o) => ({ value: o, label: o })),
    },
    {
      key: 'status_name',
      label: 'ステータス',
      type: 'multiselect',
      options: (/* istanbul ignore next -- 同上 */ selectOptions.status_name || []).filter(Boolean).map((o) => ({ value: o, label: o })),
    },
    {
      key: 'actor',
      label: '領域',
      type: 'multiselect',
      options: actorOptions.filter(Boolean).map((o) => ({ value: o, label: o })),
    },
    {
      key: 'assignee',
      label: '担当者',
      type: 'multiselect',
      options: employeeOptions.filter(Boolean).map((o) => ({ value: o, label: o })),
    },
    { key: 'date_from', label: '期限From', type: 'date' },
    { key: 'date_to', label: '期限To', type: 'date' },
    {
      key: 'keyword',
      label: 'キーワード',
      type: 'text',
      className: 'pm-filter-item--keyword',
      placeholder: 'タイトル / 依頼事項・対応内容',
    },
  ], [actorOptions, deadlineCheckOptions, oemRegionOptions, employeeOptions, selectOptions.status_name]);

  const handleExportConfirm = async (scope) => {
    const data = scope === 'filtered' ? filteredRows : flatRows;
    setExporting(true);
    try {
      await exportToExcel(data, BASE_COLUMNS, 'AI管理');
    } finally {
      setExporting(false);
      setExportPopupOpen(false);
    }
  };

  const buildAiPayload = (row) => {
    const statusId = statusByName.get(row.status_name);

    // OEM×対地 解決
    let oemId = null;
    let regionId = null;
    /* istanbul ignore else -- OEM×対地は必須かつ選択式なので未解決の値は保存前に弾かれる */
    if (row.oemRegion && oemRegionLookup.has(row.oemRegion)) {
      const resolved = oemRegionLookup.get(row.oemRegion);
      oemId = resolved.oemId;
      regionId = resolved.regionId;
    }

    /* istanbul ignore next -- 領域は必須項目なので空にならない */
    const actorIds = (row.actors_display || '')
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean)
      .map((name) => actorByName.get(name))
      .filter(Boolean);
    /* istanbul ignore next -- 担当者は必須項目なので空にならない */
    const employeeIds = (row.assignees_display || '')
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean)
      .map((name) => employeeByName.get(name))
      .filter(Boolean);

    if (!row.task || !statusId || !oemId || !regionId || actorIds.length === 0 || employeeIds.length === 0 || !row.deadline || !row.memo) {
      return null;
    }

    return {
      task: /* istanbul ignore next -- 直前のガードで必須項目が揃っている */ (row.task || ''),
      oem_id: oemId,
      region_id: regionId,
      status_id: statusId,
      actor_ids: actorIds,
      employee_ids: employeeIds,
      deadline: /* istanbul ignore next -- 同上 */ (row.deadline || null),
      memo: /* istanbul ignore next -- 同上 */ (row.memo || ''),
      updated_at_iso: row.updated_at_iso ?? null,
    };
  };

  const handlePopupDelete = async (row) => {
    /* istanbul ignore if -- 削除ボタンは既存レコード（id あり）にしか出ない */
    if (!row.id) throw new Error('新規レコードは削除できません');
    await deleteAiManagement(row.id, row.updated_at_iso ?? null);
    const aiData = await getAiManagements();
    setRawRows(aiData);
  };

  const handlePopupSave = async (row) => {
    const payload = buildAiPayload(row);
    if (!payload) throw new Error('必須項目が未入力です');
    let savedId;
    try {
      if (row.id && !row.__isNew) {
        await updateAiManagement(row.id, payload);
        savedId = row.id;
      } else {
        const created = await createAiManagement(payload);
        savedId = created?.id;
      }
    } catch (e) {
      if (e.status === 409) {
        throw new Error('他のユーザーによってこのレコードが更新されました。\n入力した内容は画面に残っています。\nOK を押した後、メモなど必要な内容をコピーしてから\nキャンセルで閉じて最新データを確認してください。');
      }
      throw e;
    }
    const aiData = await getAiManagements();
    setRawRows(aiData);
    if (savedId != null) {
      setHighlightedRowId(savedId);
      /* istanbul ignore next -- 5秒後にハイライトを解除するタイマー（jsdom で未発火） */
      setTimeout(() => setHighlightedRowId(null), 5000);
    }
  };


  const createEmptyRow = () => {
    const empty = {};
    BASE_COLUMNS.forEach((col) => {
      empty[col.key] = /* istanbul ignore next -- AI管理にチェックボックス列は無い */ (col.type === 'checkbox' ? false : '');
    });
    empty.__isNew = true;
    empty.status_name = '未着手';
    return empty;
  };

  return (
    <>
    <DashboardInputLayout
      inputTabIndex={5}
      toolbar={
        !loading && !error ? (
          <DashboardInputToolbar
            countText={`${filteredRows.length} / ${flatRows.length} 件を表示`}
            exporting={exporting}
            onAdd={() => setAddSignal((prev) => prev + 1)}
            onExport={() => setExportPopupOpen(true)}
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            showAdd
            showExport
            showFilters={showFilters}
            showFilterToggle
          />
        ) : null
      }
      filters={
        !loading && !error && showFilters ? (
          <AdvancedFilterPanel
            fields={filterFields}
            filterMode={filterMode}
            onClear={clearFilters}
            onFieldChange={handleFilterChange}
            onFilterModeChange={setFilterMode}
            values={filterValues}
          />
        ) : null
      }
    >
      {loading && <div>読み込み中...</div>}
      {!loading && error && <div>{error}</div>}
      {!loading && !error && (
        <EditableRecordTable
          addButtonLabel="+ レコード追加"
          columns={columns}
          createEmptyRow={createEmptyRow}
          enableRowPopup
          externalAddSignal={addSignal}
          highlightedRowId={highlightedRowId}
          initialRows={filteredRows}
          onPopupDelete={handlePopupDelete}
          onPopupSave={handlePopupSave}
          readOnly={!canEdit('ダッシュボード')}
          rowPopupTitle="AI管理レコード詳細"
          showAddButton={false}
        />
      )}
    </DashboardInputLayout>
    <ExportConfirmPopup
      open={exportPopupOpen}
      onClose={() => setExportPopupOpen(false)}
      onExport={handleExportConfirm}
      filteredCount={filteredRows.length}
      totalCount={flatRows.length}
      loading={exporting}
      sheetName="AI管理"
      filterFields={filterFields}
      filterValues={filterValues}
    />
    </>
  );
}
